import {dispatchApi, isChunkedUploadAvailable} from 'common/helpers/chunkedUpload'
import {
  type DataUploadConversion,
  type DataUploadDraftAnswers,
  type DataUploadUseCaseKey,
  ONBOARDING_PREFILL_DRAFT_TITLE,
  dataUploadErrorCode,
  dataUploadErrorStatus,
  dataUploadJobPercent,
  dataUploadJobStatus,
  isDataUploadJobRunning,
  newestDataUploadDraft,
  normalizeDataUploadConversion,
} from 'common/helpers/dataUpload'
import {useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {
  getDataUploadDraftJob,
  getDataUploadDraftTemplate,
  listDataUploadDrafts,
  markDataUploadDraftApplied,
  saveDataUploadDraftAnswers,
} from 'setup/redux/Actions/DataUpload/dataUpload'

/** How often a running prefill job is checked. */
const POLL_MS = 2000
/** Give up on a job that is still reading after this long (a long scan reads ~40 s a page). */
const MAX_WAIT_MS = 15 * 60 * 1000

type ApiDispatch = (action: any) => unknown

/**
 * Drafts marked applied in this tab. The server is what keeps them from being offered again
 * (an applied draft leaves the open list, across logouts and browsers); this only covers the
 * moment before its answer, when the step may open again and list the draft once more.
 */
const appliedThisVisit = new Set<string>()

/**
 * `PUT data-upload/drafts/:id/answers`, fire and forget: the user's answers so far, kept on the
 * draft so a step resumed after a logout does not ask them again. Nothing is sent without one.
 */
export const saveDraftAnswers = (
  dispatch: ApiDispatch,
  draftId: string | null | undefined,
  answers: DataUploadDraftAnswers | null | undefined
): void => {
  if (!draftId || !answers || Object.keys(answers).length === 0) return
  dispatchApi(dispatch, (ok, fail) =>
    saveDataUploadDraftAnswers(draftId, answers, ok, fail)
  ).catch(() => undefined)
}

/**
 * `POST data-upload/drafts/:id/applied`, fire and forget: the draft's rows are on the screen,
 * so no step reads and applies them again. A failure (an older server, the draft gone) only
 * means it may be offered again on a later visit, as before.
 */
export const markDraftApplied = (
  dispatch: ApiDispatch,
  draftId: string,
  answers?: DataUploadDraftAnswers | null
): void => {
  appliedThisVisit.add(draftId)
  const body = answers && Object.keys(answers).length > 0 ? answers : null
  dispatchApi(dispatch, (ok, fail) => markDataUploadDraftApplied(draftId, body, ok, fail)).catch(
    () => undefined
  )
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export interface PrefillDraft {
  draftId: string
  /** The draft as our template, re-checked against the school when it was fetched. */
  conversion: DataUploadConversion
  /**
   * Never offer this draft again (`POST …/applied`, with the step's `answers`). Already done
   * before `onDraft` unless the step asked to do it itself (`deferHandled`).
   */
  markHandled: () => void
}

interface Options {
  useCaseKey: Extract<DataUploadUseCaseKey, 'students' | 'allocation'>
  /** True while the step is open: the template is re-checked against the school when fetched. */
  active: boolean
  /** Query of the template call (`flavour`, `curriculum_column`). */
  templateParams: {flavour?: string; curriculumColumn?: boolean}
  /** Called once per draft that holds rows; the step applies them. */
  onDraft: (draft: PrefillDraft) => void
  /**
   * The step marks the draft handled itself (`draft.markHandled`), once the user is done with
   * it (imported, or removed). Until then it is offered again each time the step opens, so a
   * question skipped or a tab left does not lose the rows.
   */
  deferHandled?: boolean
  /** The step's answers to the draft's questions, sent with `markHandled` (kept on the draft). */
  answers?: () => DataUploadDraftAnswers | null | undefined
}

/**
 * Wait for a prefill draft's job, then fetch the draft as our template. Resolves with the
 * conversion, or null when the job failed, was cancelled, ran too long, or `isCancelled()`.
 *
 * @param jobStatus The status the draft list gave, if any (skips the first job call when done).
 * @param onPercent The job's `progress.percent` after each check (null until it reports one).
 * @param onJob Each job answer, for callers that read its counts.
 */
export const readPrefillDraft = async (
  dispatch: ApiDispatch,
  draftId: string,
  jobStatus: string | null,
  templateParams: {flavour?: string; curriculumColumn?: boolean},
  isCancelled: () => boolean,
  onPercent?: (percent: number | null) => void,
  onJob?: (job: unknown) => void
): Promise<DataUploadConversion | null> => {
  const startedAt = Date.now()
  let status = jobStatus
  const pause = async () => {
    if (Date.now() - startedAt > MAX_WAIT_MS) return false
    await wait(POLL_MS)
    return !isCancelled()
  }
  for (;;) {
    if (status !== 'done') {
      // The list may leave the job out (or give only its id): ask the job itself.
      const job = await dispatchApi(dispatch, (ok, fail) =>
        getDataUploadDraftJob(draftId, ok, fail)
      ).catch(() => null)
      if (isCancelled()) return null
      if (job) onJob?.(job)
      status = dataUploadJobStatus(job)
      onPercent?.(dataUploadJobPercent(job))
    }
    if (status === 'failed' || status === 'cancelled') return null
    if (isDataUploadJobRunning(status)) {
      if (!(await pause())) return null
      continue
    }
    // Done, or a status this build doesn't know: ask for the template. A job still reading
    // answers 409 JOB_RUNNING, and then the job is checked again.
    try {
      const response = await dispatchApi(dispatch, (ok, fail) =>
        getDataUploadDraftTemplate(draftId, templateParams, ok, fail)
      )
      if (isCancelled()) return null
      onPercent?.(100)
      return normalizeDataUploadConversion(response?.result ?? response)
    } catch (error) {
      const stillReading =
        dataUploadErrorCode(error) === 'JOB_RUNNING' || dataUploadErrorStatus(error) === 409
      if (!stillReading) throw error
      status = null
      if (!(await pause())) return null
    }
  }
}

/**
 * Rows the file holds for a step, whatever the school has yet: rows read in the file, else
 * written plus left out. Allocation rows are mostly "left out" on the Teachers step (their
 * teachers aren't saved yet), and still count here.
 */
export const prefillRowsFound = (conversion: DataUploadConversion | null): number => {
  if (!conversion) return 0
  const {read, written, skipped} = conversion.report
  return conversion.counts?.read ?? read ?? written + skipped.length
}

/** Rows read, as a finished job reports them (`result.report.read`, `counts.read`…), or 0. */
export const prefillJobRowsFound = (job: unknown): number => {
  const root = (job ?? {}) as Record<string, any>
  const result = root.result ?? root
  const inner = result.job ?? result
  for (const source of [result, inner.result, inner]) {
    // `counts.records`: rows the job read into the draft (node-api's job answer).
    const value = Number(
      source?.counts?.records ??
        source?.counts?.read ??
        source?.report?.read ??
        source?.read ??
        source?.rowsRead
    )
    if (Number.isFinite(value) && value > 0) return value
  }
  return 0
}

/**
 * Wait for a prefill draft's job to end, without fetching the template. Resolves with the last
 * job answer once it is `done`, or null when it failed, was cancelled, ran too long, or
 * `isCancelled()`. Enough to count its rows (`counts.records`).
 */
export const waitForPrefillJob = async (
  dispatch: ApiDispatch,
  draftId: string,
  isCancelled: () => boolean
): Promise<unknown> => {
  const startedAt = Date.now()
  for (;;) {
    const job = await dispatchApi(dispatch, (ok, fail) =>
      getDataUploadDraftJob(draftId, ok, fail)
    ).catch(() => null)
    if (isCancelled()) return null
    const status = dataUploadJobStatus(job)
    if (status === 'done') return job
    if (status === 'failed' || status === 'cancelled') return null
    if (Date.now() - startedAt > MAX_WAIT_MS) return null
    await wait(POLL_MS)
    if (isCancelled()) return null
  }
}

/**
 * The Teachers file carried forward (`POST onboarding/prefill`): when a step opens, look for its
 * newest `onboarding-prefill` draft, wait while its job is still reading, fetch it as our
 * template and hand it to the step once. A draft with no rows (a teachers-only file), a failed
 * job or no draft at all shows nothing. AI servers only.
 *
 * @returns `reading` while a draft is being read, with the job's `percent` (0..100) once it
 *   reports one. The percent moves per file, so a single file may jump 0 -> 95 -> 100.
 *   `rowsFound`: rows the job read into the draft, once it says (0 until then).
 */
export const useOnboardingPrefillDraft = ({
  useCaseKey,
  active,
  templateParams,
  onDraft,
  deferHandled = false,
  answers,
}: Options): {reading: boolean; percent: number | null; rowsFound: number} => {
  const dispatch = useDispatch()
  const [reading, setReading] = useState(false)
  const [percent, setPercent] = useState<number | null>(null)
  const [rowsFound, setRowsFound] = useState(0)
  const onDraftRef = useRef(onDraft)
  const answersRef = useRef(answers)
  useEffect(() => {
    onDraftRef.current = onDraft
    answersRef.current = answers
  })
  const {flavour, curriculumColumn} = templateParams

  useEffect(() => {
    if (!active || !isChunkedUploadAvailable()) return
    let cancelled = false

    const run = async () => {
      const list = await dispatchApi(dispatch, (ok, fail) =>
        listDataUploadDrafts({useCaseKey, title: ONBOARDING_PREFILL_DRAFT_TITLE}, ok, fail)
      )
      const draft = newestDataUploadDraft(list)
      // An applied draft is no longer listed; one applied in this tab may still be, for a moment.
      if (cancelled || !draft || appliedThisVisit.has(draft.id)) return

      setReading(true)
      setPercent(null)
      setRowsFound(0)
      const conversion = await readPrefillDraft(
        dispatch,
        draft.id,
        // A job the list already calls done is still asked once: its answer counts the rows,
        // shown while the template is fetched (a long list takes a while to re-check).
        null,
        {flavour, curriculumColumn},
        () => cancelled,
        setPercent,
        (job) => {
          const found = prefillJobRowsFound(job)
          if (found > 0) setRowsFound(found)
        }
      )
      if (cancelled) return
      const handled = () => markDraftApplied(dispatch, draft.id, answersRef.current?.())
      // Nothing written, nothing left out and nothing asked: a teachers-only file. Rows left out
      // (Allocation: teachers not saved yet) are still worth showing, with the reason; a draft
      // whose every line waits on a question ("which classes?", "which branch?") is asked.
      const empty =
        !conversion ||
        (conversion.report.written === 0 &&
          conversion.report.skipped.length === 0 &&
          conversion.branchQuestions.length === 0 &&
          conversion.allocationQuestions.length === 0)
      if (!deferHandled || empty) handled()
      if (!conversion || empty) return
      onDraftRef.current({draftId: draft.id, conversion, markHandled: handled})
    }

    run()
      // Any failure (no drafts route, 409 JOB_RUNNING after a race with a new prefill) shows
      // nothing: the step works as it always did, and the next visit looks again.
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) {
          setReading(false)
          setPercent(null)
          setRowsFound(0)
        }
      })

    return () => {
      cancelled = true
      setReading(false)
      setPercent(null)
      setRowsFound(0)
    }
  }, [active, curriculumColumn, deferHandled, dispatch, flavour, useCaseKey])

  return {reading, percent, rowsFound}
}
